/**
 * CronHealthCard — admin /admin tile answering "did the scheduled batch
 * actually fire in production?" Reads GET /api/admin/cron/health, which
 * serves run history recorded by `_wrap_leader` (server.py) into
 * `cron_runs` (60-day TTL). Built after a real incident: the Sunday
 * weekly-reminder email didn't arrive and there was no way to tell
 * whether the cron ran, was skipped by the pod-leader lock, or crashed.
 */
import { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { HeartStraight, ArrowsClockwise, CheckCircle, XCircle } from "@phosphor-icons/react";

const JOB_LABELS = {
  weekly_reminders: "Weekly reminder (Sun 5pm ET)",
  weekly_digests: "Owner weekly digest (Sun 5:30pm ET)",
  customer_weekly_digest: "Customer weekend digest (Fri 4pm ET)",
  ghost_owner_nudge: "Ghost-owner nudge (Tue 9am ET)",
  follower_digest: "Follower digest (Sun 2pm ET)",
  admin_daily_digest: "Admin daily digest (6am ET)",
};

function fmtWhen(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    }).format(new Date(iso)) + " ET";
  } catch { return iso; }
}

function summarize(result) {
  if (!result || typeof result !== "object") return "";
  return Object.entries(result)
    .filter(([, v]) => typeof v === "number" || typeof v === "string" || typeof v === "boolean")
    .slice(0, 5)
    .map(([k, v]) => `${k}: ${v}`)
    .join(" · ");
}

export const CronHealthCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/cron/health");
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <section
      className="rounded-2xl border bg-card p-4 space-y-3"
      data-testid="cron-health-card"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-display font-bold text-base flex items-center gap-2">
          <HeartStraight weight="duotone" className="h-4 w-4 text-primary" />
          Cron health
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={load}
          disabled={loading}
          className="gap-1"
          data-testid="cron-health-refresh"
        >
          <ArrowsClockwise className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Last time each scheduled batch actually ran (any pod). History starts
        from this deploy — 60-day retention.
      </p>
      {loading && !data ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !data || (data.jobs || []).length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="cron-health-empty">
          No runs recorded yet. Records appear after the first scheduled job
          fires post-deploy.
        </p>
      ) : (
        <ul className="divide-y">
          {data.jobs.map((j) => (
            <li key={j.job_id} className="py-2 flex items-start gap-2" data-testid={`cron-health-row-${j.job_id}`}>
              {j.status === "error" ? (
                <XCircle weight="fill" className="h-4 w-4 text-destructive mt-0.5 flex-none" />
              ) : (
                <CheckCircle weight="fill" className="h-4 w-4 text-emerald-500 mt-0.5 flex-none" />
              )}
              <div className="min-w-0">
                <div className="text-sm font-medium">
                  {JOB_LABELS[j.job_id] || j.job_id}
                  <span className="ml-2 text-xs text-muted-foreground">{fmtWhen(j.started_at)}</span>
                </div>
                {summarize(j.result) && (
                  <div className="text-[11px] text-muted-foreground truncate">{summarize(j.result)}</div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default CronHealthCard;
